import * as React from 'react';
import ComposeEmailFunction from '../ComposeEmailFunction';
import fetchBrandList from '../services/fetchBrandList';
import initializeLivePersonaCard from '../actions/initializeLivePersonaCard';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { isFeatureEnabled } from 'owa-feature-flags';
import personaControlStore from '../store/Store';
import { isEmailAddressBrand, isEmailAddressDecoratedBrand } from '../utils/isEmailAddressBrand';
import { isNullOrWhiteSpace } from 'owa-string-utils';
import { logUsage } from 'owa-analytics';
import { lokiClientCorrelationId } from 'owa-people-loki';
import { observer } from 'mobx-react-lite';
import { getPersonaInitialsColor } from 'office-ui-fabric-react/lib/Persona';
import isConsumerGroupAddress from '../utils/isConsumerGroupAddress';

export interface PersonaCardBehaviorInputProps {
    emailAddress: string;
    name: string;
    olsPersonaId?: string;
    personaType?: string;
    composeEmailOverride?: ComposeEmailFunction;
    disableHover?: boolean;
    ariaLabel?: string;
    className?: string;
    mailBoxType?: string;
    isUnauthenticatedSender?: boolean;
    locationToOpen?: string;
    clientScenario?: string;
    focusReturnElement?: HTMLElement;
    aadObjectId?: string;
}

export interface PersonaCardBehaviorProps extends PersonaCardBehaviorInputProps {
    tabIndex?: number;
}

interface PersonaCardBehaviorInternalProps extends PersonaCardBehaviorProps {
    children: React.ReactNode;
}

const LivePersonaCardPersonaTypes = {
    NotResolved: 'NotResolved',
    User: 'User',
    Group: 'Group',
    External: 'External',
    DistributionList: 'DistributionList',
    PrivateDistributionList: 'PrivateDistributionList',
    Location: 'Location',
    Room: 'Room',
    Meeting: 'Meeting',
    Brand: 'Brand',
};

interface PersonaInfo {
    personaType: string;
    isBrand: boolean;
    isTier3Brand: boolean;
}

const LivePersonaCardBehavior = observer(function LivePersonaCardBehavior({
    composeEmailOverride,
    isUnauthenticatedSender,
    mailBoxType,
    personaType,
    emailAddress,
    clientScenario,
    locationToOpen,
    focusReturnElement,
    tabIndex,
    olsPersonaId,
    name,
    disableHover,
    ariaLabel,
    className,
    children,
    aadObjectId,
}: PersonaCardBehaviorInternalProps) {
    React.useEffect(() => {
        initializeLivePersonaCard(composeEmailOverride);
        fetchBrandList();
    }, []);

    const calculatedPersonaType = React.useMemo(
        () => calculatePersonaType(isUnauthenticatedSender, mailBoxType, personaType, emailAddress),
        [isUnauthenticatedSender, mailBoxType, personaType, emailAddress]
    );

    const onCardOpen = React.useCallback(
        () => logInfoOnCardOpen(calculatedPersonaType, clientScenario, locationToOpen),
        [calculatedPersonaType, clientScenario, locationToOpen]
    );

    const onCardClose = React.useCallback(() => {
        if (focusReturnElement) {
            focusReturnElement.focus();
        }
    }, [focusReturnElement]);

    const personaCoinColor = getPersonaInitialsColor({ text: name });

    if (personaControlStore.isLivePersonaCardInitialized) {
        let LivePersonaCardHoverTargetV2 = (window as any).LivePersonaCardHoverTargetV2;
        return (
            <LivePersonaCardHoverTargetV2
                tabIndex={tabIndex}
                cardParameters={{
                    externalAppSessionCorrelationId: lokiClientCorrelationId,
                    personaInfo: {
                        identifiers: {
                            Smtp: emailAddress,
                            PersonaType: calculatedPersonaType.personaType,
                            OlsPersonaId: olsPersonaId,
                            AadObjectId: aadObjectId,
                        },
                        isUnauthenticatedSender: isUnauthenticatedSender,
                        displayName: name,
                        isTier3Brand: calculatedPersonaType.isTier3Brand,
                        personaCoinColor: personaCoinColor,
                    },
                    behavior: {
                        disableHover: disableHover,
                        onCardOpen: onCardOpen,
                        onCardClose: onCardClose,
                        locationToOpen: locationToOpen,
                        clientScenario: clientScenario,
                    },
                    ariaLabel: ariaLabel,
                }}>
                <span className={className} aria-label={ariaLabel}>
                    {children}
                </span>
            </LivePersonaCardHoverTargetV2>
        );
    } else {
        return <>{children}</>;
    }
});

function logInfoOnCardOpen(
    personaInfo: PersonaInfo,
    clientScenario?: string,
    locationToOpen?: string
) {
    if (personaInfo.isBrand) {
        logUsage(
            personaInfo.personaType === LivePersonaCardPersonaTypes.Brand
                ? 'BrandCardDisplayed'
                : 'BrandDetectedButNotDisplayed'
        );
    }

    const scenarioToPrint = clientScenario || 'No Client Scenario';
    const locationToPrint = locationToOpen || 'LPC';
    logUsage('LPCRenderedFromHoverTarget', {
        clientScenario: scenarioToPrint,
        locationToOpen: locationToPrint,
    });
}

function calculatePersonaType(
    isUnauthenticatedSender: boolean,
    mailBoxType: string,
    personaType: string,
    emailAddress: string
): PersonaInfo {
    if (isUnauthenticatedSender) {
        return {
            personaType: LivePersonaCardPersonaTypes.User,
            isBrand: false,
            isTier3Brand: false,
        };
    }

    if (
        isFeatureEnabled('rp-brandCards') &&
        !isNullOrWhiteSpace(emailAddress) &&
        isEmailAddressBrand(emailAddress)
    ) {
        const isTier3Brand = isEmailAddressDecoratedBrand(emailAddress);
        return {
            personaType: LivePersonaCardPersonaTypes.Brand,
            isBrand: true,
            isTier3Brand: isTier3Brand,
        };
    }

    if (!isNullOrWhiteSpace(mailBoxType)) {
        return {
            personaType: getLivePersonaCardPersonaTypeFromMailBoxType(mailBoxType),
            isBrand: false,
            isTier3Brand: false,
        };
    }

    if (
        !isNullOrWhiteSpace(personaType) &&
        Object.keys(LivePersonaCardPersonaTypes).indexOf(personaType) > -1
    ) {
        return { personaType: personaType, isBrand: false, isTier3Brand: false };
    }

    if (isConsumerGroupAddress(emailAddress)) {
        return { personaType: 'Group', isBrand: false, isTier3Brand: false };
    }

    if (isConsumer()) {
        return { personaType: 'User', isBrand: false, isTier3Brand: false };
    }

    return { personaType: 'NotResolved', isBrand: false, isTier3Brand: false };
}

function getLivePersonaCardPersonaTypeFromMailBoxType(mailBoxType: string): string {
    switch (mailBoxType) {
        case 'PublicDL':
            return LivePersonaCardPersonaTypes.DistributionList;
        case 'PrivateDL':
            return LivePersonaCardPersonaTypes.PrivateDistributionList;
        case 'GroupMailbox':
            return LivePersonaCardPersonaTypes.Group;
        case 'Mailbox':
        case 'Contact':
            return LivePersonaCardPersonaTypes.User;
        case 'OneOff':
        case 'Unknown':
            return LivePersonaCardPersonaTypes.NotResolved;
        default:
            return LivePersonaCardPersonaTypes.User;
    }
}

export function useLivePersonaCard(props: PersonaCardBehaviorProps): React.ComponentType<{}> {
    return observer(function PersonaCardBehavior({ children }: { children?: React.ReactNode }) {
        if (personaControlStore.isPersonaCardDisabled) {
            return <>{children}</>;
        } else {
            return <LivePersonaCardBehavior {...props}>{children}</LivePersonaCardBehavior>;
        }
    });
}
